import * as THREE from 'three';
import type { ModAnchors } from './modAnchors';
import type { SurfaceField } from './modSurface';

/**
 * modProfile.ts (M128b/c) — where the soft mods REST. `bodyTopAt` is the
 * anchor-table profile (roof, the rear-window slope down to the deck, the
 * deck to the tail, a pickup's bed floor, a bike's or Formula's seat);
 * `restTopAt` prefers the ride's MEASURED surface (modSurface.ts) once the
 * fitted model has landed and falls back to the table until then. The M128
 * hair had one flat floor at deck height and fell through the rear glass.
 */

const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);

/** The anchor-table height of the bodywork's top at car-local `z`; null past
 *  the tail or outboard of the flanks. `coverBed` reads a bed opening as the
 *  deck (the cape's rest, the hair's floor over the fans' tub). */
export function bodyTopAt(a: ModAnchors, x: number, z: number, coverBed = false): number | null {
  if (Math.abs(x) > a.halfW + 0.12) return null;
  if (z < a.tailZ - 0.02) return null;
  if (a.kind !== 'car') {
    const hz = a.helmetBack?.z ?? a.roofRear.z;
    if (z > hz + 0.3) return null;
    if (a.kind === 'f1' && a.fin) {
      // The Formula: the airbox top right behind the helmet, then the engine
      // cover sloping down past the shark fin to the tail (the M128 flat
      // deck sat ABOVE the helmet and shoved the hair up like a broom).
      const z0 = hz - 0.1 * a.s;
      if (z >= z0) return a.deckY;
      const fz = a.fin.z;
      const fy = a.fin.y + 0.1 * a.s;
      if (z >= fz) return a.deckY + (fy - a.deckY) * ((z0 - z) / Math.max(1e-3, z0 - fz));
      const ty = a.fin.y * 0.6;
      return fy + (ty - fy) * Math.min(1, (fz - z) / Math.max(1e-3, fz - a.tailZ));
    }
    return a.deckY;
  }
  const roofY = Math.max(a.roof.y, a.roofRear.y);
  const rrZ = a.roofRear.z;
  if (a.bed) {
    if (z <= a.bed.z1 && z >= a.bed.z0) return coverBed ? a.deckY : a.bed.floorY;
    if (z > a.bed.z1) return roofY;
    return a.deckY;
  }
  if (z >= rrZ) return roofY;
  if (z >= a.deckZ) {
    const t = (rrZ - z) / Math.max(1e-3, rrZ - a.deckZ);
    return a.roofRear.y + (a.deckY - a.roofRear.y) * t;
  }
  return a.deckY;
}

/** The surface a soft part rests on: the fitted model's measured top when
 *  the rig has it (the sports car's haunches, the monster's rails — the
 *  table never knew them), else the table. Bed openings still read as the
 *  deck under `coverBed`, and a measurement can only RAISE the table's
 *  floor over a bed (the fans sit in it, the cloth must not). */
export function restTopAt(a: ModAnchors, surf: SurfaceField | null | undefined, x: number, z: number, coverBed = false): number | null {
  const table = bodyTopAt(a, x, z, coverBed);
  if (!surf) return table;
  const measured = surf.topAt(x, z);
  if (measured === null) return table;
  if (coverBed && a.bed && z >= a.bed.z0 && z <= a.bed.z1) return Math.max(measured, a.deckY);
  return measured;
}

/** Where the bike's pillion fan sits, shared by the fans, the cape and the
 *  ponytail so every part agrees on the passenger's place: ON the seat
 *  behind the tucked rider (M128b sat it 0.3 u inside the seat and the legs
 *  reached the rear tyre — owner). M153: the rebuilt bike's raised tail deck
 *  stands 10 cm above the M128 seat height, so the passenger sat INSIDE the
 *  cowl with the thighs coming out of its flanks — the seat height is now the
 *  measured deck under the passenger (the table's deck before the model). */
export function pillionSeat(a: ModAnchors, surf?: SurfaceField | null): { base: THREE.Vector3; H: number } {
  const base = a.local(0, -0.62, 0.86);
  const deck = surf?.topIn(-0.1 * a.k, 0.1 * a.k, base.z - 0.08 * a.k, base.z + 0.08 * a.k) ?? a.deckY;
  base.y = Math.max(base.y, deck + 0.05 * a.k);
  return { base, H: 1.75 * 0.92 };
}

/** M153: the measured roof's half-width at `z` — how far out its top stays
 *  within `drop` of the crown — so a rail, a lamp bar or a clasp sits on the
 *  roof and not in the air past its curved edge; `fallback` before the model
 *  is in. */
export function roofHalfAt(surf: SurfaceField | null | undefined, z: number, drop: number, maxX: number, fallback: number): number {
  if (!surf) return fallback;
  const crown = surf.topAt(0, z);
  if (crown === null) return fallback;
  let x = 0;
  while (x < maxX) {
    const t = surf.topAt(x + 0.02, z);
    if (t === null || t < crown - drop) break;
    x += 0.02;
  }
  return x > 0.05 ? x : fallback;
}

/** How a fan sits (partsSoft.buildFan's poses). */
export type FanMode = 'stand' | 'tub' | 'edge' | 'window' | 'pillion' | 'deck';

/** M154 polish: how far a fan sat on a flat deck ('deck': knees up, boots
 *  planted) reaches toward the way it faces, from its hip to its boot tips,
 *  in units of its standing height. */
export const DECK_REACH = 0.36;

/** M154 polish r2: how far behind the cab (units of k) the monster truck's
 *  crew stands in a row when the cape lies on its deck behind them. */
export const CREW_ROW = 0.36;

/** M154 polish r3: the monster truck's seated crew's spacing (units of k)
 *  when the floodlight masts stand on the deck's corners (0.5 without). */
export const FAN_ROW_TIGHT = 0.38;

/** One cheering fan's place on a ride. */
export interface FanPlace {
  p: THREE.Vector3;
  yaw: number;
  mode: FanMode;
  /** The fan's standing height. */
  H: number;
  idx: number;
  side: number;
}

/**
 * Where the cheering fans ride (pure: the fans and the parts that keep clear
 * of them share it — M154 r2: the corner flags hid behind the monster
 * truck's tailgate crew). `mods` = the whole equipped list (the cape takes
 * decks and tailgates the fans would otherwise sit on).
 */
export function fanLayout(a: ModAnchors, surf: SurfaceField | null | undefined, mods: readonly string[]): FanPlace[] {
  const out: FanPlace[] = [];
  if (a.kind === 'f1') return out;
  const H = 1.75;
  const put = (H0: number, mode: FanMode, idx: number, side: number, p: THREE.Vector3, yaw: number): void => {
    out.push({ p, yaw, mode, H: H0, idx, side });
  };
  if (a.kind === 'bike') {
    const seat = pillionSeat(a, surf);
    put(seat.H, 'pillion', 1, 1, seat.base, 0);
  } else if (a.bed) {
    const b = a.bed;
    if (a.isMonster && mods.includes('mod.tifo')) {
      // The cape lies on the deck behind them, so the crew STANDS in a row
      // right behind the cab (M154 polish r2: in a line abreast; the middle
      // fan stood a step back, on the cloth).
      const zf = b.z1 - CREW_ROW * a.k;
      [-0.44, 0, 0.44].forEach((x, i) => put(H * 0.94, 'stand', i, 1, V(x * a.k, b.floorY, zf), Math.PI));
    } else if (a.isMonster) {
      // M154 polish: sat ON the flat deck, knees up and boots planted, far
      // enough in from the tail that nothing hangs over it (sat on the edge
      // their legs hung down over the tail's LED lamps).
      const z = a.tailZ + DECK_REACH * H + 0.06 * a.k;
      const y = surf?.topIn(-0.5 * a.k, 0.5 * a.k, z - 0.05 * a.k, z + 0.05 * a.k) ?? a.deckY;
      // (M154 polish r3: closer together with the floodlight masts on the
      // deck's corners: the masts stood among the outer fans' knees)
      const gap = mods.includes('mod.floods') ? FAN_ROW_TIGHT : 0.5;
      [-gap, 0, gap].forEach((x, i) => put(H, 'deck', i, 1, V(x * a.k, y, z), Math.PI));
    } else {
      const zm = (b.z0 + b.z1) / 2 - 0.15 * a.k;
      put(H, 'tub', 0, 1, V(-0.36 * a.k, b.floorY, b.z1 - 0.16 * a.k), Math.PI);
      put(H, 'tub', 1, 1, V(0.36 * a.k, b.floorY, b.z1 - 0.16 * a.k), Math.PI);
      put(H, 'tub', 2, 1, V(-0.52 * a.k, b.floorY, zm), Math.PI / 2);
      put(H, 'tub', 3, 1, V(0.52 * a.k, b.floorY, zm), -Math.PI / 2);
    }
  } else if (a.openTop && a.helmetBack) {
    // M153: the open muscle car. The window fans leaned out of a convertible's
    // door tops and hung OUTSIDE the car (and its roofless deck read as too
    // short for the trunk fan). Now: a fan riding in the passenger seat beside
    // the driver, sized to him, scarf up — and, unless the cape takes the
    // deck, the trunk fan sat on the tail lip facing the pack.
    const px = -a.helmetBack.x;
    const pz = a.helmetBack.z + 0.08 * a.k;
    const cushion = surf?.topIn(px - 0.08 * a.k, px + 0.08 * a.k, pz - 0.05 * a.k, pz + 0.12 * a.k) ?? a.helmetBack.y - 0.5 * a.k;
    put(1.15, 'tub', 2, 1, V(px, cushion, pz), 0);
    if (!mods.includes('mod.tifo')) {
      const z = a.tailZ + 0.16 * a.k;
      const y = surf?.topIn(-0.15, 0.15, z - 0.05, z + 0.05) ?? a.deckY;
      put(1.4, 'edge', 3, 1, V(0, y, z), Math.PI);
    }
  } else if (a.window) {
    // The window fans lean out of the side windows: the hips sit INSIDE the
    // cabin below the sill so the torso leaves through the window opening
    // (M153: from the window's glass point they started outside the door
    // and the waist came through the door skin under the window).
    for (const sx of [-1, 1]) {
      const w = a.window;
      put(H, 'window', sx > 0 ? 1 : 2, sx, V(sx * (w.x - 0.15 * a.k), w.y - 0.01 * a.k, w.z), (sx * Math.PI) / 2);
    }
    // A long flat decklid seats a third fan on the tail, legs over it, facing
    // the pack — unless the cape drapes over that deck, or a stock rear wing
    // overhangs the tail lip (M153: on the rebuilt sports car the trunk fan
    // sat under its wing blade with his boots on the road).
    const deckLen = a.roofRear.z - a.tailZ;
    if (deckLen > 1.2 * a.k && !a.wing && !mods.includes('mod.tifo')) {
      put(H, 'edge', 3, 1, V(0, a.deckY, a.tailZ + 0.16 * a.k), Math.PI);
    }
  }
  return out;
}

/** M154 r2: the room a fan takes, as an upright cylinder (centre x, z,
 *  radius, bottom and top y): the body, the raised arms and the scarf held
 *  overhead (a window fan leans out of its window). */
export function fanVolume(f: FanPlace): { x: number; z: number; r: number; y0: number; y1: number } {
  const hip = { stand: 0.5, tub: 0.02, edge: 0, window: -0.2, pillion: 0, deck: 0.04 }[f.mode] * f.H;
  // (the arms up and the scarf between the hands reach a quarter of the
  // height either side; a window fan raises one arm and leans out)
  const window = f.mode === 'window';
  const out = window ? f.side * 0.16 * f.H : 0;
  return { x: f.p.x + out, z: f.p.z, r: (window ? 0.2 : 0.27) * f.H, y0: f.p.y + Math.min(0, hip - 0.5 * f.H), y1: f.p.y + hip + 0.76 * f.H };
}

/** M154 polish r4: where a fan's head is (its centre, car-local) and its
 *  radius: the same lean, hip height and turn partsSoft.buildFan gives it
 *  (pure; the floodlights keep their lamp heads clear of it). */
export function fanHeadCentre(f: FanPlace): { c: THREE.Vector3; r: number } {
  const deg = Math.PI / 180;
  const lean = { stand: 0, tub: 14, edge: 6, window: 34, pillion: 6, deck: 8 }[f.mode] * deg;
  const hip = { stand: 0.5, tub: 0.02, edge: 0, window: -0.2, pillion: 0, deck: 0.04 }[f.mode] * f.H;
  const leanA = f.mode === 'window' ? lean : -lean;
  const c = V(0, 0.515 * f.H, 0.012 * f.H)
    .applyAxisAngle(V(1, 0, 0), leanA)
    .add(V(0, hip, 0))
    .applyAxisAngle(V(0, 1, 0), f.yaw)
    .add(f.p);
  return { c, r: 0.068 * f.H };
}

export { V as vec3 };
